import { Injectable, ViewContainerRef } from '@angular/core';
import { LocalStorageService } from '../../core/service/local-storage.service';
import {
  EsriExtent,
  EsriMap,
  EsriMapView,
  EsriCompass,
  EsriReactiveUtils
} from '../../config/esri.module';
import { BaseMapListService } from './base-map-list.service';
import { ConfigService } from '../../config/config.service';
import { ArbeitsbereichService } from './arbeitsbereich.service';
import { CONSTANTS_VALUE } from '../../config/constants.module';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Workspace } from '../enum/workspace.enum';
import { DetailViewFeatureParams } from '../../component/detail-view/detail-view.model';
import { UrlStateHelperService } from './url-state/url-state-helper.service';
import Enums from '../enum/enums';
import { LayerListService } from './layer-list.service';

/**
 * @class MainMapService
 * @description The MainMapService provides the map and the map-view
 */
@Injectable({
  providedIn: 'root',
})
export class MainMapService {
  private readonly _mainMap: __esri.Map;
  private readonly _mapView: __esri.MapView;
  private _currentWidgetPool: __esri.Expand[];

  private readonly cursorBehaviorSubject: BehaviorSubject<string> = new BehaviorSubject<string>(
    CONSTANTS_VALUE.CURSOR.default
  );

  // Notify when extent is changed
  public onExtentChangeSubject: Subject<any>;
  public selectedBaseMap: __esri.Basemap;
  public mapInteractionActivated = true;

  public rootViewContainer: ViewContainerRef;

  public get cursor(): string {
    return this.cursorBehaviorSubject.value;
  }

  get cursor$(): Observable<string> {
    return this.cursorBehaviorSubject.asObservable();
  }

  setCursor(cursorValue: string) {
    this.cursorBehaviorSubject.next(cursorValue);
  }

  constructor(
    private baseMapListService: BaseMapListService,
    private configService: ConfigService,
    private localStorageService: LocalStorageService,
    private arbeitsbereichService: ArbeitsbereichService,
    private urlStateHelperService: UrlStateHelperService,
    private layerListService: LayerListService
  ) {
    this.onExtentChangeSubject = new Subject();

    const esriLayers = this.layerListService.getMainMapLayers();
    const defBaseMap = this.baseMapListService.baseMapList.find(f => f['defaultBaseMap'] === true);
    this._currentWidgetPool = new Array<__esri.Expand>();

    // create map
    const mapPropperties: __esri.MapProperties = {
      basemap: defBaseMap,
      layers: esriLayers,
    };
    this._mainMap = new EsriMap(mapPropperties);

    // create map view
    const mapViewProperties: __esri.MapViewProperties = {
      map: this._mainMap,
      constraints: {rotationEnabled: true},
      extent: this.getInitialExtent(),
    };
    this._mapView = new EsriMapView(mapViewProperties);
    // add basic zoom tool to map
    this._mapView.ui.move(['zoom'], 'top-left');

    const compass = new EsriCompass({
      view: this._mapView,
      icon: 'absolute-direction',
    });

    // adds the compass to the top left corner of the MapView
    this._mapView.ui.add(compass, 'top-left');

    // Listen on map-click. When clicked on the main-map, all open widgets will be closed
    this._mapView.on('click', () => {
      this.closeWidgets();
    });

    // Listen to extent change - When the extent changes save it to Cookie
    EsriReactiveUtils.watch(
      () => this._mapView.extent,
      (newValue: __esri.Extent, oldValue: __esri.Extent) => {
        if (newValue !== oldValue) {
          this.onExtentChangeSubject.next(newValue);
          // anbg: gave some problems with zoom to point
          this.localStorageService.extent = newValue;
        }
      }
    );
  }

  /**
   * @name registerWidget
   * @param {__esri.Expand} widget
   * @description When a dynamic widget is created, register it
   * in the MainMapService. The Service has control over all created widgets
   * and can open/close or remove them from the DOM.
   */
  public registerWidget(widget: __esri.Expand): void {
    if (widget) {
      this._currentWidgetPool.push(widget);
    }
  }

  /**
   * removes specific widget from MainMap widgetPool
   * @param widgetName the name of the widget to remove
   */
  public removeWidgetFromPool(widgetName: string) {
    this._currentWidgetPool = this._currentWidgetPool.filter(filtered => filtered.id !== widgetName);
  }

  /**
   * @name closeDetailViewWidgets
   * @description Close the detail view widget
   */
  public closeDetailViewWidgets() {
    this._currentWidgetPool.forEach((widget: __esri.Expand) => {
      if (widget.id === CONSTANTS_VALUE.WIDGET_ID.DetailViewComponent) {
        widget.collapse();
      }
    });
  }

  /**
   * @name mapView
   * @description mapView getter
   */
  public get mapView(): __esri.MapView {
    return this._mapView;
  }

  public get mainMap(): __esri.Map {
    return this._mainMap;
  }

  /**
   * @name closeOtherWidgets
   * @description Close all opened dynamic widgets except the
   * given one.
   * @param inputWidget
   */
  public closeOtherWidgets(inputWidget: __esri.Expand) {
    this._currentWidgetPool.forEach((widget: __esri.Expand) => {
      if (widget !== inputWidget) {
        widget.collapse();
      }
    });
  }

  /**
   * @name closeWidgets
   * @description Close all opened dynamic widgets
   */
  public closeWidgets() {
    if (this._currentWidgetPool.length === 0) return;
    this._currentWidgetPool.forEach((widget: __esri.Expand) => {
      if (widget.id === CONSTANTS_VALUE.WIDGET_ID.DetailViewComponent || this.mapInteractionActivated === false) {
        return;
      }
      widget.collapse();
    });
  }

  public handleNavigationByDetailView(layerName: string, selectedFeature: DetailViewFeatureParams) {
    const ws = this.arbeitsbereichService.selectedArbeitsbereich.id as Workspace;
    this.urlStateHelperService.setState({
      workspace: ws,
      tool: Enums.ToolCode.info,
      layer: layerName.toLowerCase(),
      id: this.getObjectId(selectedFeature.feature.attributes),
      selectedFeature: selectedFeature,
    });
  }

  public setRootViewContainerRef(viewContainerRef: ViewContainerRef) {
    this.rootViewContainer = viewContainerRef;
  }

  private getObjectId(attributes: any): number {
    const objectIdKey = Object.keys(attributes).find(key => key.toLowerCase() === 'objectid');
    return objectIdKey ? attributes[objectIdKey] : null;
  }

  /**
   * @name getInitialExtent
   * @returns {__esri.Extent}
   * @description Returns the default extent stored in the config
   */
  private getInitialExtent(): __esri.Extent {
    let extent = this.localStorageService.extent;
    if (!extent) {
      // default extent
      extent = new EsriExtent(this.configService.mainMapConfig.extent);
    }
    return extent;
  }

  public activateMapInteraction() {
    this.mapInteractionActivated = true;
  }

  public deactivateMapInteraction() {
    this.mapInteractionActivated = false;
  }
}
